/**
 * RiskFlagDismissDialog — Revenue Recovery R3 (§40.13 ruling 1): the reason a
 * person gives for dismissing a risk flag, from the FIXED list, with an
 * optional note that "Other" requires. The note is 1–200 characters, is never
 * copied into evidence and is never read by R7; the input says so with its
 * hint. The dialog is the DeleteDealDialog shape (aria-modal, Escape cancels,
 * focus lands on the first control) so the single-key guard parks J / K / L
 * while it is open.
 */
import { useEffect, useRef, useState } from 'react'
import { accent, ink, overlay } from '../../../../theme/tokens'
import { DISMISS_REASONS, NOTE_HINT, NOTE_MAX } from './dealHealth'

export function RiskFlagDismissDialog({ ruleLabel, submitting, onCancel, onSubmit }: {
  /** The check's label ("Recent activity"), for the title. */
  ruleLabel: string
  submitting: boolean
  onCancel: () => void
  onSubmit: (reason: string, note: string) => void
}) {
  const [reason, setReason] = useState<string>('')
  const [note, setNote] = useState('')
  const firstRef = useRef<HTMLInputElement>(null)
  useEffect(() => { firstRef.current?.focus() }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])
  const trimmed = note.trim()
  const needsNote = reason === 'other'
  const valid = reason !== '' && (!needsNote || trimmed.length > 0) && trimmed.length <= NOTE_MAX
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: overlay.scrim }}>
      <form role="dialog" aria-modal="true" aria-labelledby="dismiss-flag-title" data-dismiss-dialog
        className="k-card w-full max-w-md p-6 flex flex-col gap-4"
        onSubmit={e => { e.preventDefault(); if (valid && !submitting) onSubmit(reason, trimmed) }}>
        <h2 id="dismiss-flag-title" className="text-16 font-semibold text-ink-primary">Dismiss {ruleLabel} flag</h2>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-13 text-ink-secondary mb-1">Why is this not a risk right now?</legend>
          {DISMISS_REASONS.map((r, i) => (
            <label key={r.slug} className="flex items-center gap-2 text-13 text-ink-primary">
              <input ref={i === 0 ? firstRef : undefined} type="radio" name="dismiss-reason" value={r.slug}
                checked={reason === r.slug} onChange={() => setReason(r.slug)} />
              {r.label}
            </label>
          ))}
        </fieldset>
        <label className="flex flex-col gap-1 text-13 text-ink-secondary">
          <span>Note{needsNote ? ' (required for Other)' : ' (optional)'}</span>
          <textarea className="k-input min-h-[72px]" value={note} maxLength={NOTE_MAX} rows={3}
            aria-describedby="dismiss-note-hint" onChange={e => setNote(e.target.value)} />
          <span id="dismiss-note-hint" className="flex justify-between text-12 text-ink-muted">
            <span>{NOTE_HINT}</span>
            <span aria-live="polite">{trimmed.length}/{NOTE_MAX}</span>
          </span>
        </label>
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="k-btn-secondary" disabled={submitting} onClick={onCancel}>Cancel</button>
          <button type="submit" className="k-btn disabled:opacity-60" disabled={!valid || submitting}
            style={{ background: accent.DEFAULT, color: ink['on-accent'] }}>
            {submitting ? 'Dismissing…' : 'Dismiss flag'}
          </button>
        </div>
      </form>
    </div>
  )
}
